import { DraftSnapshot, StoredDraft } from './draftStorage';
import { Post } from './api';

export interface Recovered {
  snapshot: DraftSnapshot;
  // True when the recovered content differs from the live post — drives the
  // "Recovered an unsaved draft" banner with its Discard action.
  differsFromLive: boolean;
}

function liveSnapshot(post: Post): DraftSnapshot {
  return {
    title: post.title,
    featured_image_url: post.featured_image_url,
    body_html: post.body_html,
    published: post.published,
  };
}

function sameContent(a: DraftSnapshot, b: DraftSnapshot): boolean {
  return (
    a.title === b.title &&
    a.body_html === b.body_html &&
    (a.featured_image_url ?? null) === (b.featured_image_url ?? null)
  );
}

// Pick the newest of {local draft, backend draft shadow, live post}. Precedence
// (from the spec): local wins if it exists AND (there is no backend draft OR local
// is newer than draft_updated_at); else the backend draft; else the live post.
export function recoverDraft(post: Post | undefined, local: StoredDraft | null): Recovered {
  const backendDraft = post?.draft ?? null;
  const backendTs = post?.draft_updated_at ? Date.parse(post.draft_updated_at) : -Infinity;

  if (local && (!backendDraft || local.savedAt > backendTs)) {
    return {
      snapshot: local.snapshot,
      differsFromLive: post ? !sameContent(local.snapshot, liveSnapshot(post)) : true,
    };
  }
  if (backendDraft) {
    // The backend draft shadow is content-only (no `published`); a draft is only ever
    // parked on an already-published post, so inherit the live post's published flag
    // (defaulting false when there's no live post). Normalise to a full DraftSnapshot.
    const snapshot: DraftSnapshot = {
      title: backendDraft.title,
      featured_image_url: backendDraft.featured_image_url ?? null,
      body_html: backendDraft.body_html,
      published: post?.published ?? false,
    };
    return {
      snapshot,
      differsFromLive: post ? !sameContent(snapshot, liveSnapshot(post)) : true,
    };
  }
  if (post) {
    return { snapshot: liveSnapshot(post), differsFromLive: false };
  }
  return {
    snapshot: { title: '', featured_image_url: null, body_html: '', published: false },
    differsFromLive: false,
  };
}
